import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router';

import { BillingAccessBlockedModal } from '~components/commons/BillingSuspensionBlockingModal/BillingAccessBlockedModal';
import { BillingDelinquencyModal } from '~components/commons/BillingSuspensionBlockingModal/BillingDelinquencyModal';

import { useBillingDelinquency } from '~hooks/billing/useBillingDelinquency';

import { ROLES } from '~constants';
import { BILLING_PATH } from '~constants/billing/billingPaths';
import { ConfiningBillingDelinquencyTier } from '~constants/billing/delinquency';
import { BillingConfinementContext } from '~contexts/BillingConfinementContext';
import { GlobalState } from '~reducers';
import { isConfiningTier } from '~utils/billing/billingDelinquencyTier';
import { isBillingPath } from '~utils/billing/billingPaths';

/** Non-Owners get a modal instead of a redirect: BILLING_PATH is Owner-only. */
export const BillingDelinquencyGate = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const { pathname } = useLocation();
	const delinquencyTier = useBillingDelinquency();
	const tier = isConfiningTier(delinquencyTier) ? delinquencyTier : null;
	// The store, not localStorage: it stays correct across an impersonation swap.
	const roleName = useSelector((state: GlobalState) => state.session.roleName);
	// State, not storage: a reload or a new log-in shows the modal again.
	const [dismissedTier, setDismissedTier] =
		useState<ConfiningBillingDelinquencyTier | null>(null);
	const reopen = useCallback(() => setDismissedTier(null), []);

	const confinement = useMemo(() => (tier ? { tier, reopen } : null), [tier, reopen]);

	if (tier && roleName !== ROLES.owner) {
		return <BillingAccessBlockedModal tier={tier} />;
	}

	if (tier && !isBillingPath(pathname)) {
		return <Navigate to={BILLING_PATH} replace />;
	}

	// The same tree with or without a tier, so the page is not remounted when the flags land.
	return (
		<BillingConfinementContext.Provider value={confinement}>
			{children}
			{tier && dismissedTier !== tier && (
				<BillingDelinquencyModal
					tier={tier}
					onDismiss={() => setDismissedTier(tier)}
				/>
			)}
		</BillingConfinementContext.Provider>
	);
};
